import React, { useState, useEffect, useRef } from 'react';
import { Box, Button } from '@codeparva/ui-components-library';
import { useFormik } from 'formik';
import { loginFormSchema } from './formValidationSchema';
import { UserFlowTypes } from '..';
import { UserFlowError } from './UserFlowError';
import { Icon } from '../../../utilities/icomoon-css';

import { Link, navigate } from 'gatsby';
import { SessionExpired } from './SessionExpired';
import { useWindowDimensions } from '../../../utilities/Dimensions';
import { tabletscreensize } from '../../../utilities/constants';
import { USER_FLOW_CONSTANTS } from '../constants/userFlowsConstants';

export const LoginForm = ({
  EncryptPass,
  bannerMessage,
  sessionExpired,
  locationDetails,
  loggedInSmsFacilityId,
  setLoggedInSmsFacilityId,
  historyLocation,
}: UserFlowTypes.TLoginForm) => {
  const { width } = useWindowDimensions();
  const [error, setError] = React.useState<any>();
  const [hasError, setHasError] = React.useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [mappedLocationDetails, setMappedLocationDetails] = useState<{
    [key: string]: string;
  }>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mappedData: { [key: string]: string } = {};
    locationDetails &&
      locationDetails.length &&
      locationDetails.map(
        ({
          facility_sms_id,
          location_city,
          location_state,
          location_name,
        }: UserFlowTypes.TLocationDetail) => {
          mappedData[
            facility_sms_id
          ] = `${location_name}, ${location_city}, ${location_state}`;
        }
      );
    setMappedLocationDetails(mappedData);
  }, [locationDetails]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        dropdownRef?.current &&
        !dropdownRef?.current?.contains(event?.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const LocationDropdown = () => {
    const toggleDropdown = () => {
      setIsOpen(!isOpen);
    };

    const handleOptionClick = (option: string) => {
      const { facility_sms_id: smsFacilityId, ...restLocation } =
        historyLocation?.state || {};
      const selectedLocationDetailsArray = mappedLocationDetails[option].split(
        ','
      );
      setLoggedInSmsFacilityId(option);
      setIsOpen(false);
      if (smsFacilityId)
        navigate(
          `../../../../${mappedLocationDetails[smsFacilityId]
            .split(',')[2]
            .trim()}/${mappedLocationDetails[smsFacilityId]
            .split(',')[1]
            .trim()}/account/login`,
          {
            state: { restLocation },
          }
        );
      if (selectedLocationDetailsArray.length)
        navigate(
          `../../../../${selectedLocationDetailsArray[2].trim()}/${selectedLocationDetailsArray[1].trim()}/account/login`
        );
    };

    return (
      <div className="dropdownContainer">
        <label className="locationLabel">
          {USER_FLOW_CONSTANTS.selectLocation}
        </label>
        <div
          className={`dropdown ${isOpen ? 'is-active' : ''}`}
          ref={dropdownRef}
        >
          <div className="dropdown-trigger">
            <button
              className="button dropdownButton"
              aria-haspopup="true"
              aria-controls="dropdown-menu"
              onClick={toggleDropdown}
            >
              <div className="is-flex is-align-items-center dropdownInput">
                <div className="contentEnclosure">
                  <Icon
                    name={'tfw-geo-1-cp'}
                    size={16}
                    title={'tfw-geo-1-cp'}
                  />
                  <span className="dropdownContent">
                    {mappedLocationDetails[loggedInSmsFacilityId]}
                  </span>
                </div>
                <Icon
                  name={
                    isOpen
                      ? 'tfw-arrow-up-bold-2-cp'
                      : 'tfw-arrow-down-bold-2-cp'
                  }
                  size={16}
                  title={'arrow-down-1'}
                />
              </div>
            </button>
          </div>
          <div
            className="dropdown-menu locationDropdownMenu"
            id="dropdown-menu"
            role="menu"
          >
            <div className="dropdown-content">
              {locationDetails?.map(
                (
                  eachLocation: UserFlowTypes.TLocationDetail,
                  index: number
                ) => (
                  <div
                    onClick={() =>
                      handleOptionClick(eachLocation.facility_sms_id)
                    }
                    className={'dropdown-item dropdownItem'}
                    key={index}
                  >
                    {mappedLocationDetails[eachLocation.facility_sms_id]}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Login = () => {
    const [passwordShow, setPasswordShow] = useState(false);
    const togglePassword = () => {
      setPasswordShow(!passwordShow);
    };

    const formik = useFormik({
      initialValues: {
        userName: '',
        password: '',
      },
      validationSchema: loginFormSchema,

      onSubmit: values => {
        EncryptPass(values.userName, values.password, setError, setHasError);
      },
    });

    return (
      <form onSubmit={formik.handleSubmit}>
        <label className="titleColor">
          Username
          <span className="requiredLoginAsterisk">*</span>
        </label>
        <br></br>
        <input
          id="userName"
          name="userName"
          type="email"
          placeholder="email id"
          className="inputLoginField"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.userName}
          data-testid="input-username"
        />
        {formik.touched.userName && formik.errors.userName ? (
          <div className="errorLoginSection">
            <div className="errorLoginIconSpace">
              <Icon name="tfw-danger-triangle-cp" size={12} color="red" />
            </div>
            <div className="errorLoginMessage content1">
              {formik.errors.userName}
            </div>
          </div>
        ) : (
          <></>
        )}

        <label className="titleColor">
          Password
          <span className="requiredLoginAsterisk">*</span>
        </label>
        <br></br>
        <div className="control has-icons-right">
          <input
            id="password"
            name="password"
            type={passwordShow ? 'text' : 'password'}
            placeholder="password"
            className="is-flex inputLoginField"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
            data-testid="input-password"
          />
          <span className="icon is-small is-right">
            <span className="is-clickable" onClick={() => togglePassword()}>
              {passwordShow ? (
                <Icon name="tfw-show-bold-cp" color="black" size={14} />
              ) : (
                <Icon name="tfw-hide-bold-cp" color="black" size={14} />
              )}
            </span>
          </span>
        </div>
        {formik.touched.password && formik.errors.password ? (
          <div className="errorLoginSection">
            <div className="errorLoginIconSpace">
              <Icon name="tfw-danger-triangle-cp" size={12} color="red" />
            </div>
            <div className="errorLoginMessage content1">
              {formik.errors.password}
            </div>
          </div>
        ) : (
          <></>
        )}
        <Link
          to="../forgot-password"
          state={{ navigatedFrom: location.pathname }}
          className="forgotPasswordLink"
        >
          Forgot Password?
        </Link>
        {UserFlowError(hasError, error)}
        <div className="ctaButton">
          <Button
            className="cp-primary-button-highlighted buttonColor"
            data-testid="login-btn-pay-rent"
            disabled={!formik.isValid || !formik.dirty}
          >
            Sign In
          </Button>
        </div>
      </form>
    );
  };

  return (
    <>
      <div>
        {bannerMessage && (
          <div className="loginBanner py-4">
            <div className="bannerData">
              <span className="is-flex pr-2">
                <Icon
                  name="tfw-danger-triangle-cp"
                  size={width <= tabletscreensize ? 48 : 32}
                  color="yellow"
                ></Icon>
              </span>

              <p>{bannerMessage || ''}</p>
            </div>
          </div>
        )}
        <div className="parentLoginContainer">
          <div className="loginChildContainer">
            <div className="titleContainer">
              <h3>Sign In</h3>
            </div>
            {locationDetails?.length > 1 && <LocationDropdown />}
            <Box className="boxLoginPadding">
              {sessionExpired ? <SessionExpired /> : <></>}
              <Login />
              {sessionExpired ? (
                <div className="pt-4">
                  <Link
                    to="/"
                    state={{ navigatedFrom: location.pathname }}
                    className="forgotPasswordLink"
                  >
                    Go back to home
                  </Link>
                </div>
              ) : (
                <></>
              )}
            </Box>
          </div>
        </div>
      </div>
    </>
  );
};
